export interface CropRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface CropOffsetBounds {
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
}

export interface CropSourceRectInput {
  imageWidth: number;
  imageHeight: number;
  canvasWidth: number;
  canvasHeight: number;
  zoom: number;
  offsetX: number;
  offsetY: number;
}

export type ImageCropPurpose = 'store-cover' | 'store-logo' | 'package-cover';

export interface CropPreset {
  aspectRatio: number;
  outputWidth: number;
  outputHeight: number;
  mimeType: 'image/jpeg' | 'image/png';
}

export function getCropPreset(purpose: ImageCropPurpose): CropPreset {
  if (purpose === 'store-logo') return { aspectRatio: 1, outputWidth: 800, outputHeight: 800, mimeType: 'image/png' };
  return { aspectRatio: 16 / 9, outputWidth: 1600, outputHeight: 900, mimeType: 'image/jpeg' };
}

export function getCropRect(imageWidth: number, imageHeight: number, aspectRatio: number): CropRect {
  if (!(imageWidth > 0) || !(imageHeight > 0) || !(aspectRatio > 0)) throw new Error('INVALID_CROP_GEOMETRY');
  const imageRatio = imageWidth / imageHeight;
  const width = imageRatio >= aspectRatio ? imageHeight * aspectRatio : imageWidth;
  const height = imageRatio >= aspectRatio ? imageHeight : imageWidth / aspectRatio;
  return { x: (imageWidth - width) / 2, y: (imageHeight - height) / 2, width, height };
}

export function clampCropOffset(x: number, y: number, bounds: CropOffsetBounds): { x: number; y: number } {
  return {
    x: Math.min(bounds.maxX, Math.max(bounds.minX, x)),
    y: Math.min(bounds.maxY, Math.max(bounds.minY, y)),
  };
}

export function getCropSourceRect(input: CropSourceRectInput): CropRect {
  const { imageWidth, imageHeight, canvasWidth, canvasHeight, zoom } = input;
  if (!(imageWidth > 0) || !(imageHeight > 0) || !(canvasWidth > 0) || !(canvasHeight > 0) || !(zoom >= 1)) {
    throw new Error('INVALID_CROP_GEOMETRY');
  }
  const scale = Math.max(canvasWidth / imageWidth, canvasHeight / imageHeight) * zoom;
  const drawnWidth = imageWidth * scale;
  const drawnHeight = imageHeight * scale;
  const bounds: CropOffsetBounds = {
    minX: (canvasWidth - drawnWidth) / 2,
    maxX: (drawnWidth - canvasWidth) / 2,
    minY: (canvasHeight - drawnHeight) / 2,
    maxY: (drawnHeight - canvasHeight) / 2,
  };
  const offset = clampCropOffset(input.offsetX, input.offsetY, bounds);
  const drawnX = (canvasWidth - drawnWidth) / 2 + offset.x;
  const drawnY = (canvasHeight - drawnHeight) / 2 + offset.y;
  const width = canvasWidth / scale;
  const height = canvasHeight / scale;
  return {
    x: Math.max(0, Math.min(imageWidth - width, -drawnX / scale)),
    y: Math.max(0, Math.min(imageHeight - height, -drawnY / scale)),
    width,
    height,
  };
}

export function getOutputFileName(filename: string, mimeType: string): string {
  const base = filename.replace(/\.[^.]+$/, '') || 'cropped-image';
  return `${base}.${mimeType === 'image/png' ? 'png' : 'jpg'}`;
}
